import {useEffect,useRef,useState} from "react";
import {serializeTechPackCollections,type TechPackDoc} from "../schema.js";
import {useStudio} from "./StudioContext.js";
import {getFreshToken} from "./auth.js";
import {formLabel as label,seasonYears} from "./techpack_form_contract.js";
import {BTN} from "./shared.js";

type State={id?:string;style_number?:string;status?:string;current_version_id?:string;can_manage?:boolean;versions?:{id:string;version_no:number;locked_at:string}[]};
type Code={code:string;label_en?:string;label_zh?:string;status:string};
// Single fetch wrapper for the canonical endpoints, shared by the component
// and by the exported lock flow below.
async function canonicalRequest(roomId:string,lang:string,action:string,body?:unknown){
  const token=await getFreshToken();
  const response=await fetch(`/api/studio/techpacks/${roomId}/canonical/${action}`,{method:body===undefined?"GET":"POST",headers:{"Content-Type":"application/json",Authorization:`Bearer ${token||""}`},body:body===undefined?undefined:JSON.stringify(body)});
  if(!response.ok)throw new Error(label("canonical_failed",lang));
  return response;
}
// Display mirrors only. The server never trusts these fields as identity.
export function mirrorCanonicalState(techPack:TechPackDoc,next:{style_number?:string;current_version_id?:string},lang:string,userId:string){
  techPack.ydoc.transact(()=>{
    for(const[key,value] of Object.entries({style_number:next.style_number||"",_canonical_version_id:next.current_version_id||""})){
      if((key!=="style_number"||next.style_number) && techPack.metadata.get(key)?.value!==value)techPack.metadata.set(key,{value,language_code:lang,last_edited_by:userId,timestamp:Date.now()});
    }
  });
}
// THE lock flow: confirm -> POST lock -> mirror the fresh canonical state into
// metadata._canonical_version_id. CanonicalActions' Lock button AND the style
// header's Version control both route here -- there is no second lock path.
// Resolves false when the user cancels the confirm.
let sharedLockKey:string|null=null;
export async function lockCanonicalVersion(techPack:TechPackDoc,roomId:string,userId:string,lang:string):Promise<boolean>{
  if(!window.confirm(label("lock_confirm",lang)))return false;
  sharedLockKey??=crypto.randomUUID();
  await canonicalRequest(roomId,lang,"lock",{document:serializeTechPackCollections(techPack),idempotency_key:sharedLockKey});
  sharedLockKey=null;
  mirrorCanonicalState(techPack,await(await canonicalRequest(roomId,lang,"state")).json(),lang,userId);
  return true;
}
const segments=["brand","season","gender","series","fabric","color","category"];
export function CanonicalActions(){
  const {techPack,roomId,userId,languageCode:lang}=useStudio();
  const[state,setState]=useState<State>({}),[registry,setRegistry]=useState<Record<string,Code[]>|null>(null);
  const[values,setValues]=useState<Record<string,string>>({}),[busy,setBusy]=useState(false),[error,setError]=useState("");
  const[pdfLang,setPdfLang]=useState(lang),[history,setHistory]=useState(false);
  const bindKey=useRef<string|null>(null);
  async function request(action:string,body?:unknown){return canonicalRequest(roomId,lang,action,body);}
  async function refresh(){
    const next=await (await request("state")).json() as State;
    setState(next);
    mirrorCanonicalState(techPack,next,lang,userId);
  }
  useEffect(()=>{void refresh().catch(()=>setError(label("canonical_failed",lang)));},[roomId]);
  async function act(fn:()=>Promise<void>){if(busy)return;setBusy(true);setError("");try{await fn();}catch{setError(label("canonical_failed",lang));}finally{setBusy(false);}}
  async function bind(){
    if(!state.id&&!window.confirm(label("bind_charge",lang)))return;
    bindKey.current??=crypto.randomUUID();
    const result=await (await request("bind",{...Object.fromEntries(segments.map(s=>[s+"_code",values[s]])),acknowledge_credit:true,idempotency_key:bindKey.current})).json();
    if(result.ok){bindKey.current=null;setRegistry(null);await refresh();}
  }
  async function lock(){
    if(await lockCanonicalVersion(techPack,roomId,userId,lang))await refresh();
  }
  async function exportDocument(){
    const response=await request(`document?lang=${encodeURIComponent(pdfLang)}`);
    const url=URL.createObjectURL(new Blob([await response.text()],{type:"text/html;charset=utf-8"}));
    const page=window.open(url,"_blank");
    if(!page){const a=document.createElement("a");a.href=url;a.download="techpack.html";a.click();}
    setTimeout(()=>URL.revokeObjectURL(url),60000);
  }
  return <>
    <span style={{fontSize:11}}>{label(state.status||"draft",lang)} · {label(state.style_number?"bound":"unbound",lang)}</span>
    <button style={{...BTN,color:"#ededed"}} disabled={busy||!state.can_manage||!!state.style_number} onClick={()=>void act(async()=>{const data=await(await request("registry")).json();setRegistry(data.segments);})}>{label("bind",lang)}</button>
    <button style={{...BTN,color:"#ededed"}} disabled={busy||!state.can_manage||!state.style_number} onClick={()=>void act(lock)}>{label("lock",lang)}</button>
    <button style={{...BTN,color:"#ededed"}} onClick={()=>setHistory(!history)}>{label("version_history",lang)}</button>
    <select className="sf-pdf-language" aria-label={label("pdf_language",lang)} value={pdfLang} onChange={e=>setPdfLang(e.target.value)}>{["en","fr","it","zh","both"].map(l=><option key={l} value={l}>{l==="zh"?"中文":l==="both"?"EN + 中文":l.toUpperCase()}</option>)}</select>
    <button style={BTN} disabled={busy||!state.can_manage||!state.current_version_id} onClick={()=>void act(exportDocument)}>{label("export",lang)}</button>
    {error&&<span role="alert">{error}</span>}
    {history&&<div style={{flexBasis:"100%"}}>{(state.versions||[]).map(v=><div key={v.id}>{label("version",lang)} {v.version_no} · {v.locked_at}</div>)}</div>}
    {registry&&<div role="dialog" aria-label={label("bind",lang)} style={{position:"fixed",inset:"15% 20%",overflow:"auto",padding:24,background:"var(--bg)",border:"1px solid #666",zIndex:80}}>
      {segments.map(segment=><label key={segment} style={{display:"block",marginBottom:8}}>{label(segment==="gender"?"gender_segment":segment,lang)}
        <select aria-label={label(segment==="gender"?"gender_segment":segment,lang)} className="sf-select" value={values[segment]||""} onChange={e=>setValues({...values,[segment]:e.target.value})}>
          <option value="">{label("select",lang)}</option>
          {(segment==="season"?seasonYears().flatMap(year=>["SS","AW"].map(t=>({code:t+year.slice(-2),status:"adopted"}))):(registry[segment]||[])).filter(c=>c.status!=="deprecated").map(c=><option key={c.code} value={c.code}>{c.code}{segment==="season"?"":` · ${lang==="zh"?(c as Code).label_zh||(c as Code).label_en:(c as Code).label_en}`}</option>)}
        </select></label>)}
      <button style={BTN} disabled={busy||segments.some(s=>!values[s])} onClick={()=>void act(bind)}>{label("confirm",lang)}</button>
      <button style={BTN} onClick={()=>setRegistry(null)}>{label("cancel",lang)}</button>
    </div>}
  </>;
}
